<template>
  <div class="resource-supplier-container">
    <ds-page-header :title="$t('resourceSupplierManage.page.title')" :subtitle="$t('resourceSupplierManage.page.subtitle')">
      <template #extra>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd">
          {{ $t('resourceSupplierManage.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="searchForm.rsId" clearable class="w-full"
            :placeholder="$t('resourceSupplierManage.supplierNo')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="searchForm.supplierName" clearable class="w-full"
            :placeholder="$t('resourceSupplierManage.supplierName')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="searchForm.tel" clearable class="w-full"
            :placeholder="$t('resourceSupplierManage.phone')" @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>
      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('resourceSupplierManage.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('resourceSupplierManage.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false"
        :show-refresh="true" @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('resourceSupplierManage.supplier') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border
        :empty-description="$t('resourceSupplierManage.page.empty')" @retry="getList">
        <el-table-column prop="rsId" :label="$t('resourceSupplierManage.no')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.rsId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="supplierName" :label="$t('resourceSupplierManage.name')" align="center" min-width="140" />
        <el-table-column prop="address" :label="$t('resourceSupplierManage.address')" align="center" min-width="160" show-overflow-tooltip />
        <el-table-column prop="tel" :label="$t('resourceSupplierManage.contact')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.tel || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="contactName" :label="$t('resourceSupplierManage.contactName')" align="center" width="110" />
        <el-table-column prop="accountBank" :label="$t('resourceSupplierManage.bank')" align="center" min-width="120" />
        <el-table-column prop="bankAccountNumber" :label="$t('resourceSupplierManage.account')" align="center" min-width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.bankAccountNumber || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('resourceSupplierManage.remark')" align="center" min-width="140" show-overflow-tooltip />
        <el-table-column :label="$t('resourceSupplierManage.operation')" align="center" width="170" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="handleEdit(scope.row)">
              {{ $t('resourceSupplierManage.edit') }}
            </el-button>
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">
              {{ $t('resourceSupplierManage.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :page="pagination.current" :size="pagination.size" :total="pagination.total"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <add-resource-supplier ref="addDialog" @success="handleSuccess" />
    <edit-resource-supplier ref="editDialog" @success="handleSuccess" />
    <delete-resource-supplier ref="deleteDialog" @success="handleSuccess" />
  </div>
</template>

<script>
import { listResourceSuppliers } from '@/api/resource/resourceSupplierManageApi'
import AddResourceSupplier from '@/components/resource/AddResourceSupplier'
import EditResourceSupplier from '@/components/resource/EditResourceSupplier'
import DeleteResourceSupplier from '@/components/resource/DeleteResourceSupplier'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ResourceSupplierManageList',
  components: {
    AddResourceSupplier,
    EditResourceSupplier,
    DeleteResourceSupplier,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        rsId: '',
        supplierName: '',
        tel: ''
      },
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          ...this.searchForm,
          communityId: getCommunityId()
        }
        const { data, total } = await listResourceSuppliers(params)
        this.tableData = data
        this.pagination.total = total
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('common.loadError')
        this.$message.error(error.message)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        rsId: '',
        supplierName: '',
        tel: ''
      }
      this.handleSearch()
    },
    handleAdd() {
      this.$refs.addDialog.open()
    },
    handleEdit(row) {
      this.$refs.editDialog.open(row)
    },
    handleDelete(row) {
      this.$refs.deleteDialog.open(row)
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.resource-supplier-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.w-full {
  width: 100%;
}
</style>
